<template>
    <div class="relative z-20 flex-shrink-0 flex h-16 bg-white shadow">
        <button aria-label="Open sidebar" class="px-4 text-gray-500 focus:outline-none focus:bg-gray-100 focus:text-gray-600 md:hidden" @click="$emit('toggleSidebar')">
            <svg-vue class="h-6 w-6" icon="font-awesome.bars-regular"></svg-vue>
        </button>
        <div class="flex-1 min-w-0 px-4 flex items-center justify-between gap-3">
            <div v-if="$store.state.user" class="min-w-0 flex-1 pr-1">
                <div class="text-left text-base sm:text-l font-semibold text-gray-900 truncate">
                    {{ greetingText }}, {{ $store.state.user.name }}
                </div>
            </div>
            <div class="flex">
                <div class="ml-4 flex-1 flex items-center md:ml-6">
                    <div v-on-clickaway="closeNotifications" class="relative">
                        <button
                            class="p-2 text-gray-500 rounded-full hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:shadow-outline"
                            type="button"
                            aria-label="Notifications"
                            @click="toggleNotifications"
                        >
                            <svg-vue class="h-6 w-6 p-px" icon="font-awesome.bell-regular"></svg-vue>
                            <span
                                v-if="notifications.unread_total > 0"
                                class="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-4 h-4 px-1 rounded-full bg-red-600 text-white text-xs leading-none"
                            >
                                {{ notifications.unread_total > 99 ? '99+' : notifications.unread_total }}
                            </span>
                        </button>
                        <transition
                            duration="100"
                            enter-active-class="transition ease-out duration-100"
                            enter-class="transform opacity-0 scale-95"
                            enter-to-class="transform opacity-100 scale-100"
                            leave-active-class="transition ease-in duration-75"
                            leave-class="transform opacity-100 scale-100"
                            leave-to-class="transform opacity-0 scale-95"
                        >
                            <div v-show="notificationsOpen" class="origin-top-right z-20 absolute right-0 mt-2 w-96 rounded-md shadow-lg" style="max-width: 95vw;">
                                <div class="rounded-md bg-white shadow-xs border border-gray-100">
                                    <div class="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                                        <div class="text-sm font-semibold text-gray-800">{{ $t('Notifications') }}</div>
                                        <div class="flex items-center gap-3">
                                            <router-link
                                                class="text-xs text-gray-500 hover:text-gray-700"
                                                to="/dashboard/notifications"
                                                @click.native="notificationsOpen = false"
                                            >
                                                {{ $t('View all') }}
                                            </router-link>
                                            <button
                                                v-if="notifications.unread_total > 0"
                                                class="text-xs text-blue-600 hover:text-blue-800"
                                                type="button"
                                                @click="markAllNotificationsRead"
                                            >
                                                {{ $t('Mark all as read') }}
                                            </button>
                                        </div>
                                    </div>
                                    <div v-if="notificationGroups.length < 1" class="px-4 py-6 text-sm text-gray-500">
                                        {{ $t('No notifications yet') }}
                                    </div>
                                    <div v-else class="overflow-y-auto" style="max-height: 420px;">
                                        <div
                                            v-for="group in notificationGroups"
                                            :key="group.key"
                                            class="border-b border-gray-100"
                                        >
                                            <button
                                                class="w-full px-4 py-3 text-left hover:bg-gray-50"
                                                type="button"
                                                @click="toggleNotificationGroup(group.key)"
                                            >
                                                <div class="flex items-start justify-between gap-3">
                                                    <div class="flex items-start min-w-0 flex-1">
                                                        <img :src="notificationIcon" alt="Notification icon" class="h-8 w-8 rounded-md border border-gray-200 bg-white p-1 mr-3 flex-shrink-0" />
                                                        <div class="min-w-0 flex-1">
                                                            <div class="flex items-center gap-2 min-w-0">
                                                                <div :class="group.unreadCount > 0 ? 'text-gray-900 font-semibold' : 'text-gray-800'" class="text-sm truncate">
                                                                    {{ group.headerTitle }}
                                                                </div>
                                                                <span class="text-xs text-gray-400 whitespace-nowrap">{{ group.items.length }} item{{ group.items.length > 1 ? 's' : '' }}</span>
                                                            </div>
                                                            <div class="text-xs text-gray-500 mt-1 truncate">
                                                                {{ group.appTitle }}
                                                            </div>
                                                            <div class="text-xs text-gray-600 mt-1 truncate">
                                                                {{ group.latestPreview }}
                                                            </div>
                                                            <div class="text-xs text-gray-400 mt-1">
                                                                {{ group.latestCreatedAt | momentFormatDateTimeAgo }}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div class="flex items-center gap-2 flex-shrink-0">
                                                        <div v-if="group.unreadCount > 0" class="min-w-5 h-5 px-1 rounded-full bg-blue-500 text-white text-[11px] leading-5 text-center">
                                                            {{ group.unreadCount > 99 ? '99+' : group.unreadCount }}
                                                        </div>
                                                        <span class="text-xs text-blue-600">{{ isNotificationGroupExpanded(group.key) ? $t('Hide') : $t('Expand') }}</span>
                                                    </div>
                                                </div>
                                            </button>
                                            <div v-if="isNotificationGroupExpanded(group.key)" class="bg-gray-50 border-t border-gray-100">
                                                <div
                                                    v-for="item in group.items"
                                                    :key="item.id"
                                                    class="px-4 py-3 border-b border-gray-100 last:border-b-0 hover:bg-white"
                                                >
                                                    <div class="flex items-start justify-between gap-3">
                                                        <button class="flex-1 text-left min-w-0" type="button" @click="openNotification(item)">
                                                            <div v-if="item.sender_name" :class="item.read_at ? 'text-gray-700' : 'text-gray-900 font-semibold'" class="text-sm truncate">
                                                                {{ item.sender_name }}
                                                            </div>
                                                            <div :class="item.read_at ? 'text-gray-600' : 'text-gray-900 font-medium'" class="text-sm break-words">
                                                                {{ stripSenderPrefix(item.message_preview || item.message, item.sender_name) }}
                                                            </div>
                                                            <div class="text-xs text-gray-400 mt-1">
                                                                {{ item.created_at | momentFormatDateTimeAgo }}
                                                            </div>
                                                        </button>
                                                        <div class="flex items-start gap-2 flex-shrink-0">
                                                            <button
                                                                v-if="item.message_full && item.message_full !== (item.message_preview || item.message)"
                                                                class="text-xs text-blue-600 hover:text-blue-800"
                                                                type="button"
                                                                @click.stop="toggleNotificationExpanded(item.id)"
                                                            >
                                                                {{ isNotificationExpanded(item.id) ? $t('Hide') : $t('Expand') }}
                                                            </button>
                                                            <div v-if="!item.read_at" class="h-2.5 w-2.5 rounded-full bg-blue-500 mt-1.5"></div>
                                                        </div>
                                                    </div>
                                                    <div
                                                        v-if="isNotificationExpanded(item.id)"
                                                        class="mt-2 rounded-md bg-white border border-gray-200 px-3 py-2 text-xs text-gray-700 whitespace-pre-line"
                                                    >
                                                        {{ stripSenderPrefix(item.message_full, item.sender_name) }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </transition>
                    </div>
                    <div v-on-clickaway="closeUserDropdown" class="ml-3 relative">
                        <button
                            id="user-menu"
                            aria-haspopup="true"
                            aria-label="User menu"
                            class="max-w-xs flex items-center text-sm rounded-full focus:outline-none focus:shadow-outline"
                            @click="toggleUserDropdown"
                        >
                            <img
                                :src="$store.state.user.avatar === 'gravatar' ? $store.state.user.gravatar : $store.state.user.avatar"
                                alt="User avatar"
                                class="h-8 w-8 rounded-full object-cover"
                            />
                        </button>
                        <transition
                            duration="100"
                            enter-active-class="transition ease-out duration-100"
                            enter-class="transform opacity-0 scale-95"
                            enter-to-class="transform opacity-100 scale-100"
                            leave-active-class="transition ease-in duration-75"
                            leave-class="transform opacity-100 scale-100"
                            leave-to-class="transform opacity-0 scale-95"
                        >
                            <div v-show="dropdownOpen" class="origin-top-right z-10 absolute right-0 mt-2 w-56 rounded-md shadow-lg">
                                <div aria-labelledby="user-menu" aria-orientation="vertical" class="py-1 rounded-md bg-white shadow-xs" role="menu">
                                    <div class="flex items-center px-4 ce py-2 border-b border-gray-100">
                                        <img
                                            :src="$store.state.user.avatar === 'gravatar' ? $store.state.user.gravatar : $store.state.user.avatar"
                                            alt="User avatar"
                                            class="h-8 w-8 mr-3 align-middle rounded-full object-cover"
                                        />
                                        <div class="w-40">
                                            <div class="text-sm font-medium truncate text-gray-800">{{ $store.state.user.name }}</div>
                                            <div class="text-xs truncate text-gray-500">{{ $store.state.user.email }}</div>
                                        </div>
                                    </div>
                                    <router-link
                                        v-if="$store.state.user ? $store.state.user.role.dashboard_access : false"
                                        class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150"
                                        role="menuitem"
                                        to="/dashboard/home"
                                        @click.native="dropdownOpen = false"
                                    >
                                        {{ $t('Dashboard') }}
                                    </router-link>
                                    <router-link
                                        class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150"
                                        role="menuitem"
                                        :to="{path: '/dashboard/tickets', query: {view: 'my'}}"
                                        @click.native="dropdownOpen = false"
                                    >
                                        {{ $t('My tickets') }}
                                    </router-link>
                                    <router-link
                                        class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150"
                                        role="menuitem"
                                        to="/account"
                                        @click.native="dropdownOpen = false"
                                    >
                                        {{ $t('Account settings') }}
                                    </router-link>
                                    <a
                                        class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150"
                                        href="/auth/logout"
                                        role="menuitem"
                                        @click.prevent="signOut"
                                    >
                                        {{ $t('Sign out') }}
                                    </a>
                                </div>
                            </div>
                        </transition>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {mixin as clickaway} from "vue-clickaway";

export default {
    name: "navbar",
    mixins: [clickaway],
    data() {
        return {
            dropdownOpen: false,
            notificationsOpen: false,
            expandedNotificationId: null,
            expandedNotificationGroupKey: null,
            notifications: {
                items: [],
                unread_total: 0,
                unread_by_view: {
                    my: 0,
                    work: 0,
                    approve: 0,
                }
            },
            poller: null,
            syncChannel: null,
            storageSyncHandler: null,
            realtimeChannel: null,
        }
    },
    mounted() {
        this.getNotifications();
        this.poller = setInterval(() => {
            if (!document.hidden) {
                this.getNotifications(true);
            }
        }, 5000);
        window.addEventListener('focus', this.onWindowFocus);
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        window.addEventListener('dashboard-refresh-notifications', this.onRefreshNotifications);
        this.initNotificationSync();
        this.syncRealtimeNotifications();
    },
    beforeDestroy() {
        if (this.poller) {
            clearInterval(this.poller);
            this.poller = null;
        }
        window.removeEventListener('focus', this.onWindowFocus);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        window.removeEventListener('dashboard-refresh-notifications', this.onRefreshNotifications);
        if (this.syncChannel) {
            this.syncChannel.close();
            this.syncChannel = null;
        }
        if (this.storageSyncHandler) {
            window.removeEventListener('storage', this.storageSyncHandler);
            this.storageSyncHandler = null;
        }
        this.stopRealtimeNotifications();
    },
    filters: {
        momentFormatDateTimeAgo: function (value) {
            return moment(value).locale(window.app.app_date_locale).fromNow();
        },
    },
    computed: {
        greetingText() {
            const hour = new Date().getHours();
            if (hour < 12) {
                return this.$t('Good morning');
            }
            if (hour < 18) {
                return this.$t('Good afternoon');
            }
            return this.$t('Good evening');
        },
        currentUserId() {
            return this.$store.state.user ? this.$store.state.user.id : null;
        },
        notificationIcon() {
            const base = window.app && window.app.url ? window.app.url : window.location.origin;
            return base.replace(/\/$/, '') + '/favicon.ico';
        },
        notificationGroups() {
            const groups = [];
            const map = {};
            (this.notifications.items || []).forEach((item) => {
                const key = item.ticket_uuid ? ('ticket:' + item.ticket_uuid) : ('notification:' + item.id);
                if (!map[key]) {
                    map[key] = {
                        key: key,
                        ticket_uuid: item.ticket_uuid || null,
                        headerTitle: item.subject_line || item.title,
                        appTitle: item.title,
                        latestPreview: item.message_preview || item.message,
                        latestCreatedAt: item.created_at,
                        unreadCount: 0,
                        items: []
                    };
                    groups.push(map[key]);
                }
                map[key].items.push(item);
                if (!item.read_at) {
                    map[key].unreadCount++;
                }
            });
            return groups;
        }
    },
    watch: {
        currentUserId() {
            this.syncRealtimeNotifications();
        }
    },
    methods: {
        signOut() {
            this.dropdownOpen = false;
            this.notificationsOpen = false;
            this.$store.commit('logout');
            this.$router.push('/auth/login');
        },
        closeUserDropdown() {
            this.dropdownOpen = false;
        },
        closeNotifications() {
            this.notificationsOpen = false;
            this.expandedNotificationId = null;
            this.expandedNotificationGroupKey = null;
        },
        toggleUserDropdown() {
            this.notificationsOpen = false;
            this.dropdownOpen = !this.dropdownOpen;
        },
        toggleNotifications() {
            this.dropdownOpen = false;
            this.notificationsOpen = !this.notificationsOpen;
            if (!this.notificationsOpen) {
                this.expandedNotificationId = null;
                this.expandedNotificationGroupKey = null;
            }
            if (this.notificationsOpen) {
                this.getNotifications();
            }
        },
        getNotifications(silent = false) {
            const self = this;
            axios.get('api/dashboard/notifications', {params: {limit: 12}}).then(function (response) {
                self.notifications = response.data;
                self.$store.commit('setNotificationUnreadTotal', response.data.unread_total || 0);
                window.dispatchEvent(new CustomEvent('dashboard-notifications-updated', {detail: response.data}));
                self.publishNotificationSync(response.data.unread_total || 0);
            }).catch(function () {
                if (!silent) {
                    self.notificationsOpen = false;
                }
            });
        },
        openNotification(item) {
            const self = this;
            const goToTarget = function () {
                self.notificationsOpen = false;
                self.expandedNotificationId = null;
                self.expandedNotificationGroupKey = null;
                if (item.url) {
                    self.$router.push(item.url);
                }
            };
            if (item.read_at) {
                goToTarget();
                return;
            }
            axios.post('api/dashboard/notifications/' + item.id + '/mark-read').then(function () {
                self.getNotifications(true);
                goToTarget();
            }).catch(function () {
                goToTarget();
            });
        },
        markAllNotificationsRead() {
            const self = this;
            axios.post('api/dashboard/notifications/mark-all-read').then(function () {
                self.getNotifications(true);
            });
        },
        toggleNotificationExpanded(id) {
            this.expandedNotificationId = this.expandedNotificationId === id ? null : id;
        },
        stripSenderPrefix(text, senderName) {
            const value = String(text || '');
            const sender = String(senderName || '').trim();
            if (!sender) {
                return value;
            }
            const escaped = sender.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            return value.replace(new RegExp('^' + escaped + '\\s*:\\s*'), '');
        },
        isNotificationExpanded(id) {
            return this.expandedNotificationId === id;
        },
        toggleNotificationGroup(key) {
            this.expandedNotificationGroupKey = this.expandedNotificationGroupKey === key ? null : key;
        },
        isNotificationGroupExpanded(key) {
            return this.expandedNotificationGroupKey === key;
        },
        onWindowFocus() {
            this.getNotifications(true);
        },
        onVisibilityChange() {
            if (!document.hidden) {
                this.getNotifications(true);
            }
        },
        onRefreshNotifications() {
            this.getNotifications(true);
        },
        initNotificationSync() {
            const self = this;
            if (window.BroadcastChannel) {
                self.syncChannel = new BroadcastChannel('ticketing-notifications');
                self.syncChannel.onmessage = function (event) {
                    const count = event && event.data ? parseInt(event.data.unread_total, 10) : 0;
                    self.$store.commit('setNotificationUnreadTotal', Number.isNaN(count) ? 0 : count);
                };
                return;
            }
            self.storageSyncHandler = function (event) {
                if (event.key !== 'ticketing-notifications-unread' || !event.newValue) {
                    return;
                }
                const count = parseInt(event.newValue, 10);
                self.$store.commit('setNotificationUnreadTotal', Number.isNaN(count) ? 0 : count);
            };
            window.addEventListener('storage', self.storageSyncHandler);
        },
        publishNotificationSync(unreadTotal) {
            if (this.syncChannel) {
                this.syncChannel.postMessage({unread_total: unreadTotal});
            } else {
                localStorage.setItem('ticketing-notifications-unread', String(unreadTotal));
            }
        },
        syncRealtimeNotifications() {
            this.stopRealtimeNotifications();
            if (!window.Echo || !this.currentUserId) {
                return;
            }
            this.realtimeChannel = window.Echo.private('users.' + this.currentUserId + '.tickets')
                .listen('.ticket.updated', () => {
                    this.getNotifications(true);
                });
        },
        stopRealtimeNotifications() {
            if (window.Echo && this.currentUserId) {
                window.Echo.leave('private-users.' + this.currentUserId + '.tickets');
            }
            this.realtimeChannel = null;
        }
    }
}
</script>

